import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Formik as FormikBase, Form as FormBase, Field } from "formik";
import { toastr } from "react-redux-toastr";
import {
  Grid,
  Button,
  Typography,
  Alert,
  Checkbox,
  FormControlLabel,
  Box,
  Tooltip,
  Icon,
} from "@mui/material";
import RpFormikTextField from "@/components/muiCustom/textField/RpFormikTextField";
import RpMUIDialog from "@/components/modals/RpMUIModal";
import RpFormikAutocomplete from "@/components/muiCustom/select/RpFormikAutocomplete";
import RpFieldLabel from "@/components/muiCustom/label/RpFieldLabel";
import FullScreenLoader from "@/components/loaders/FullScreenLoader";
import { CircularProgress, InputAdornment } from "@mui/material";
import { buildLocationFormSchema } from "../utils/locationManagementValidators";
import { locationManagementService } from "../services/locationManagementService";
import { useZipLookup } from "../hooks/useZipLookup";
import {
  LOCATION_COUNTRY_OPTIONS,
  LOCATION_FIELD_GRID_SIZE,
  LOCATION_RADIUS_MAX,
  LOCATION_RADIUS_MIN,
  PARTICIPANT_MODE,
  TARGETING_TYPE,
  TARGETING_TYPE_OPTIONS,
} from "../utils/locationManagementConstants";
import { GlobalContext } from "@/helpers/contexts/contexts";
import ParticipantFilter from "@/features/campaigns/campaign-overview/components/ParticipantFilter";
import RpSvgIcon from "@/components/icons/RpSvgIcon";
import { shouldBlockUnsafeSubmission } from "@/helpers/sanitizeHelper";
import LocationEditHistoryModal, {
  getLocationEditHistorySummary,
} from "./LocationEditHistoryModal";
import LocationFormGroupFilter from "./LocationFormGroupFilter";

type AnyRecord = Record<string, any>;

const Formik = FormikBase as React.ElementType;
const Form = FormBase as React.ElementType;

const LOCATION_FIELD_TOOLTIPS = {
  storeLocationNumber:
    "A location-specific identifier used to distinguish individual stores or locations (e.g., store numbers or franchise IDs).",
  geotarget:
    "Zip codes added to Zip Code Targeting settings to refine targeting beyond the selected radius and focus on specific geographic areas.",
  locationTags:
    "A way to organize locations by shared attributes or characteristics, such as drive-through availability, pricing tier, or other common traits.",
};

const FieldGridItem = ({
  label,
  required,
  helperText,
  children,
  tooltip,
  labelClassName,
}: {
  label: ReactNode;
  required?: boolean;
  helperText?: ReactNode;
  children: ReactNode;
  tooltip?: ReactNode;
  labelClassName?: string;
}) => (
  <Grid size={LOCATION_FIELD_GRID_SIZE}>
    <RpFieldLabel
      className={labelClassName || ""}
      required={required}
      sx={{
        display: "flex",
        alignItems: "center",
        marginBottom: required ? "" : "2px",
      }}
    >
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
      >
        {label}
        {tooltip ? (
          <Tooltip
            title={<strong>{tooltip}</strong>}
            arrow
            placement="top"
            slotProps={{
              popper: { disablePortal: false, style: { zIndex: 99999 } },
              tooltip: {
                sx: {
                  backgroundColor: "var(--neutral-white)",
                  color: "var(--palette-text-primary)",
                  fontSize: "9px",
                  padding: "6px 12px",
                  boxShadow: "var(--shadow-floating)",
                  "& .MuiTooltip-arrow": { color: "var(--neutral-white)" },
                },
              },
            }}
          >
            <Icon
              component={RpSvgIcon}
              name="info_circle_i"
              fontSize="small"
              color="primary"
              style={{ width: 15, height: 15 }}
              className="ml-1 cursor-p"
            />
          </Tooltip>
        ) : null}
      </Box>
    </RpFieldLabel>
    {children}
    {helperText && (
      <Typography variant="caption" color="text.secondary">
        {helperText}
      </Typography>
    )}
  </Grid>
);

const FormikConfiguredField = ({
  label,
  required = false,
  helperText,
  tooltip,
  labelClassName,
  render,
  component = RpFormikTextField,
  ...fieldProps
}: AnyRecord) => (
  <FieldGridItem
    label={label}
    required={required}
    helperText={helperText}
    tooltip={tooltip}
    labelClassName={labelClassName}
  >
    {render ? render() : <Field {...fieldProps} component={component} />}
  </FieldGridItem>
);

const LOCATION_NO_DATA_MESSAGES = {
  country: "No countries found.",
  group: "No groups found.",
};

const TARGETING_SAME_AS_LOCATION_OPTIONS = [
  { label: "Yes", value: 1 },
  { label: "No", value: 0 },
];

const trimPayloadValue = (value) =>
  typeof value === "string" ? value.trim() : value;

const toOptionalNumber = (value) =>
  value === "" || value == null ? null : Number(value);

const normalizeTargetingSameAsLocation = (value) => {
  if (value === 0) return 0;
  return 1;
};

const toTargetingSameAsLocationPayloadValue = (value) =>
  normalizeTargetingSameAsLocation(value);

const hasTargetingValue = (value) =>
  value !== "" && value != null && String(value).trim() !== "";

const inferTargetingType = (location) => {
  if (
    TARGETING_TYPE_OPTIONS.some(
      (option) => option.value === location?.targeting_type
    )
  ) {
    return location.targeting_type;
  }

  const hasRadius = hasTargetingValue(location?.radius);
  const hasZipCodes = hasTargetingValue(location?.geotarget);

  if (hasRadius && hasZipCodes) return TARGETING_TYPE.RADIUS_AND_ZIP;
  if (hasZipCodes) return TARGETING_TYPE.ZIP;
  if (hasRadius) return TARGETING_TYPE.RADIUS;
  return "";
};

const LocationFormFields = ({
  values,
  touched,
  errors,
  setFieldValue,
  setFieldTouched,
  isSubmitting,
  onClose,
  location,
  countryOptions,
  onCountryChange,
  groupOptions,
  groupSections,
  participantOptions,
  onParticipantListEndReached,
  brandId,
  lockedGroupIds,
}: AnyRecord) => {
  const [isEditHistoryOpen, setIsEditHistoryOpen] = useState(false);
  const { handleZipChange, isLookingUp } = useZipLookup(
    values.country,
    setFieldValue,
    setFieldTouched
  );
  const isEditMode = !!location?.fk_location_id;
  const groupOptionsWithLockedState = useMemo(() => {
    const options = [
      ...(location?.groups || [])
        .map((group) => ({
          value: String(group.fk_group_id || ""),
          label: group.group_name,
        }))
        .filter((group) => group.value && group.label),
    ];

    groupOptions.forEach((option) => {
      if (!options.some((item) => item.value === option.value)) {
        options.push(option);
      }
    });

    return options.map((option) => ({
      ...option,
      disabled: lockedGroupIds.includes(String(option.value)),
    }));
  }, [groupOptions, location?.groups, lockedGroupIds]);
  const isExistingParticipantMode =
    values.participant_mode === PARTICIPANT_MODE.EXISTING;
  const isNewParticipantMode = values.participant_mode === PARTICIPANT_MODE.NEW;
  const isTargetingSameAsLocation =
    normalizeTargetingSameAsLocation(values.targeting_same_as_location) === 1;
  const isBlankTargeting = !values.targeting_type;
  const isRadiusTargeting = values.targeting_type === TARGETING_TYPE.RADIUS;
  const isZipTargeting = values.targeting_type === TARGETING_TYPE.ZIP;
  const isRadiusAndZipTargeting =
    values.targeting_type === TARGETING_TYPE.RADIUS_AND_ZIP;
  const hasSelectedGroups = (values.group_id || []).length > 0;
  const groupErrorText =
    touched.group_id && !hasSelectedGroups ? errors.group_id : "";

  useEffect(() => {
    if (!isTargetingSameAsLocation) return;

    const nextTargetingLatitude = values.location_lat ?? "";
    const nextTargetingLongitude = values.location_long ?? "";

    if (values.targeting_latitude !== nextTargetingLatitude) {
      setFieldValue("targeting_latitude", nextTargetingLatitude, false);
    }
    if (values.targeting_longitude !== nextTargetingLongitude) {
      setFieldValue("targeting_longitude", nextTargetingLongitude, false);
    }
  }, [
    isTargetingSameAsLocation,
    setFieldValue,
    values.location_lat,
    values.location_long,
    values.targeting_latitude,
    values.targeting_longitude,
  ]);

  const resetParticipantFields = () => {
    [
      "participant_id",
      "participant_first_name",
      "participant_last_name",
      "participant_brand_id",
      "email_address",
      "phone",
    ].forEach((key) => setFieldValue(key, ""));
  };

  const handleExistingParticipantChange = async (val) => {
    setFieldValue("existing_participant_id", val || "");

    if (!val) {
      resetParticipantFields();
      return;
    }

    try {
      const res = await locationManagementService.getParticipantDetail(
        brandId,
        val
      );
      const p = res.data?.data || res.data || {};
      setFieldValue("participant_id", p.system_generated_participant_id || "");
      setFieldValue(
        "participant_first_name",
        p.participant_first_name || p.first_name || ""
      );
      setFieldValue(
        "participant_last_name",
        p.participant_last_name || p.last_name || ""
      );
      setFieldValue("participant_brand_id", p.participant_unique_id || "");
      setFieldValue("email_address", p.participant_email || p.email || "");
      setFieldValue("phone", p.participant_phone || "");
    } catch (_e) {
      resetParticipantFields();
    }
  };

  const locationFields = [
    ...(isEditMode
      ? [
          {
            name: "id",
            label: "Location ID",
            disabled: true,
            helperText: "System generated, no editable",
          },
        ]
      : []),
    {
      name: "location_brand_id",
      label: "Store/Location Number (Unique ID)",
      required: false,
      tooltip: LOCATION_FIELD_TOOLTIPS.storeLocationNumber,
    },
    { name: "location_name", label: "Location Name", required: true },
    {
      name: "country",
      label: "Country",
      required: true,
      component: RpFormikAutocomplete,
      options: countryOptions,
      onChange: (val) => onCountryChange(val, setFieldValue),
      noOptionsText: LOCATION_NO_DATA_MESSAGES.country,
    },
    {
      name: "location_street_address",
      label: "Location Street Address",
      required: true,
    },
    {
      name: "zip_code",
      label: "Zip Code",
      required: true,
      render: () => (
        <>
          <Field
            name="zip_code"
            component={RpFormikTextField}
            disabled={!values.country}
            onKeyDown={(e) => {
              if (
                !/^[a-zA-Z0-9]$/.test(e.key) &&
                ![
                  "Backspace",
                  "Delete",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                  "Home",
                  "End",
                ].includes(e.key)
              ) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              const clean = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
              setFieldValue("zip_code", clean, true);
              setFieldTouched("zip_code", true, false);
              handleZipChange(clean);
            }}
            slotProps={{
              input: {
                endAdornment: isLookingUp ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null,
              },
            }}
          />
          {isLookingUp && (
            <Typography variant="caption" color="text.secondary">
              Looking up zip code...
            </Typography>
          )}
          {!values.country && (
            <Typography variant="caption" color="text.secondary">
              Select a country first to enter ZIP code.
            </Typography>
          )}
        </>
      ),
    },
    {
      name: "state",
      label: "State",
      required: true,
      render: () => (
        <Field name="state" component={RpFormikTextField} disabled />
      ),
    },
    { name: "city", label: "City", required: true },
    {
      name: "group_id",
      label: "Groups",
      required: true,
      tooltip: LOCATION_FIELD_TOOLTIPS.locationTags,
      render: () => (
        <LocationFormGroupFilter
          groupSections={groupSections}
          groupOptions={groupOptionsWithLockedState}
          value={values.group_id}
          lockedGroupIds={lockedGroupIds}
          noOptionsText={LOCATION_NO_DATA_MESSAGES.group}
          onChange={(val) =>
            setFieldValue(
              "group_id",
              [
                ...lockedGroupIds,
                ...(val || []).filter(
                  (value) => !lockedGroupIds.includes(String(value))
                ),
              ].map(String)
            )
          }
          onBlur={() => setFieldTouched("group_id", true)}
          error={Boolean(groupErrorText)}
          helperText={groupErrorText}
        />
      ),
    },
    { name: "landing_page_url", label: "Landing Page URL", required: true },
    {
      name: "targeting_type",
      label: "Targeting Type",
      required: false,
      component: RpFormikAutocomplete,
      options: TARGETING_TYPE_OPTIONS,
      placeholder: "Please Select",
      inputReadOnly: true,
      disableClearable: false,
      onChange: (val) => {
        if (!val) {
          setFieldValue("radius", "");
          setFieldValue("geotarget", "");
        }
        if (val === TARGETING_TYPE.RADIUS) {
          setFieldValue("geotarget", "");
        }
        if (val === TARGETING_TYPE.ZIP) {
          setFieldValue("radius", "");
        }
      },
    },
    {
      name: "radius",
      label: "Radius Targeting",
      required: isRadiusTargeting || isRadiusAndZipTargeting,
      render: () => (
        <Field name="radius">
          {({ field, form }) => (
            <RpFormikTextField
              field={field}
              form={form}
              disabled={isBlankTargeting || isZipTargeting}
              inputProps={{
                "aria-label": "Radius Targeting",
                inputMode: "numeric",
                pattern: "[0-9]*",
                min: LOCATION_RADIUS_MIN,
                max: LOCATION_RADIUS_MAX,
                step: 1,
              }}
              onChange={(e) => {
                const clean = e.target.value.replace(/\D/g, "");
                form.setFieldValue("radius", clean, true);
                form.setFieldTouched("radius", true, false);
              }}
            />
          )}
        </Field>
      ),
    },
    {
      name: "geotarget",
      label: "Zip Code Targeting",
      required: isZipTargeting || isRadiusAndZipTargeting,
      disabled: isBlankTargeting || isRadiusTargeting,
      tooltip: LOCATION_FIELD_TOOLTIPS.geotarget,
    },
    {
      name: "franchise_group",
      label: "Franchise Group",
    },
    {
      name: "dma",
      label: "DMA",
    },
    {
      name: "competitors",
      label: "Competitors",
    },
    {
      name: "local_context",
      label: "Local Context",
    },
    {
      name: "primary_business_goal",
      label: "Primary Business Goal",
    },
    {
      name: "daypart_focus",
      label: "Daypart Focus",
    },
    {
      name: "location_lat",
      label: "Latitude",
    },
    {
      name: "location_long",
      label: "Longitude",
    },
    {
      name: "targeting_same_as_location",
      label: "Targeting Same as Location",
      component: RpFormikAutocomplete,
      options: TARGETING_SAME_AS_LOCATION_OPTIONS,
      inputReadOnly: true,
    },
    {
      name: "targeting_latitude",
      label: "Targeting Latitude",
      disabled: isTargetingSameAsLocation,
    },
    {
      name: "targeting_longitude",
      label: "Targeting Longitude",
      disabled: isTargetingSameAsLocation,
    },
    ...(isEditMode
      ? [
          {
            name: "created_date",
            label: "Created Date",
            disabled: true,
          },
          {
            name: "last_updated_date",
            label: "Last Update Date",
            disabled: true,
          },
          {
            name: "edit_history",
            label: "Edit History",
            render: () => (
              <Box
                component="button"
                type="button"
                onClick={() => setIsEditHistoryOpen(true)}
                sx={{
                  appearance: "none",
                  border: 0,
                  background: "none",
                  color: "primary.main",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: 1.4,
                  minHeight: 40,
                  p: 0,
                  textAlign: "left",
                  textDecorationColor: (theme) =>
                    `${theme.palette.primary.main}73`,
                  textDecorationLine: "underline",
                  textDecorationThickness: "1px",
                  textUnderlineOffset: "2px",
                  wordBreak: "break-word",
                }}
              >
                {getLocationEditHistorySummary(location)}
              </Box>
            ),
          },
        ]
      : []),
  ];

  const participantBaseFields = [
    {
      name: "participant_first_name",
      label: "Participant First Name",
      required: isEditMode || isNewParticipantMode,
      disabled: !isEditMode && isExistingParticipantMode,
    },
    {
      name: "participant_last_name",
      label: "Participant Last Name",
      required: isEditMode || isNewParticipantMode,
      disabled: !isEditMode && isExistingParticipantMode,
    },
    {
      name: "email_address",
      label: "Email Address",
      required: isEditMode || isNewParticipantMode,
      type: "email",
      disabled: !isEditMode && isExistingParticipantMode,
    },
    {
      name: "phone",
      label: "Phone Number",
      disabled: !isEditMode && isExistingParticipantMode,
    },
  ];

  const participantFields = [
    ...(isEditMode
      ? [
          {
            name: "participant_id",
            label: "Participant ID",
            disabled: true,
            helperText: "System generated, no editable",
          },
        ]
      : []),
    ...(!isEditMode && isExistingParticipantMode
      ? [
          {
            name: "existing_participant_id",
            label: "Select Participant",
            required: true,
            labelClassName: "mb-2",
            render: () => {
              const errorText =
                touched.existing_participant_id &&
                errors.existing_participant_id
                  ? errors.existing_participant_id
                  : "";

              return (
                <ParticipantFilter
                  participants={participantOptions}
                  value={values.existing_participant_id || ""}
                  onChange={(value) => {
                    setFieldTouched("existing_participant_id", true, false);
                    handleExistingParticipantChange(value ? String(value) : "");
                  }}
                  error={!!errorText}
                  helperText={errorText}
                  required
                  searchPlaceholder="Search.."
                  onListEndReached={onParticipantListEndReached}
                />
              );
            },
          },
        ]
      : []),
    ...participantBaseFields,
  ];

  return (
    <Form className="primary-selection-theme">
      {errors.general && (
        <Alert severity="error" className="mb-3">
          {errors.general}
        </Alert>
      )}

      <Grid container columnSpacing={2.5} rowSpacing={4}>
        {locationFields.map((fieldConfig) => (
          <FormikConfiguredField key={fieldConfig.name} {...fieldConfig} />
        ))}
      </Grid>

      <Typography
        variant="h6"
        fontWeight={700}
        sx={{ mt: 5, mb: 2, color: "var(--neutral-700)" }}
      >
        Participant
      </Typography>

      {!isEditMode && (
        <Grid container spacing={2} alignItems="center" className="mb-2">
          <Grid size="auto">
            <FormControlLabel
              control={
                <Checkbox
                  checked={isExistingParticipantMode}
                  onChange={() => {
                    if (isExistingParticipantMode) return;
                    setFieldValue(
                      "participant_mode",
                      PARTICIPANT_MODE.EXISTING
                    );
                    resetParticipantFields();
                  }}
                />
              }
              label="Assign location to existing participant"
            />
          </Grid>
          <Grid size="auto">
            <FormControlLabel
              control={
                <Checkbox
                  checked={isNewParticipantMode}
                  onChange={() => {
                    if (isNewParticipantMode) return;
                    setFieldValue("participant_mode", PARTICIPANT_MODE.NEW);
                    setFieldValue("existing_participant_id", "");
                    resetParticipantFields();
                  }}
                />
              }
              label="Create new participant"
            />
          </Grid>
        </Grid>
      )}

      <Grid container columnSpacing={2.5} rowSpacing={4}>
        {participantFields.map((fieldConfig) => (
          <FormikConfiguredField key={fieldConfig.name} {...fieldConfig} />
        ))}
      </Grid>

      <Grid container justifyContent="flex-end" spacing={1.5} className="mt-4">
        <Grid>
          <Button
            variant="outlined"
            size="large"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{ minWidth: 220, px: 4 }}
          >
            Cancel
          </Button>
        </Grid>
        <Grid>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={isSubmitting}
            sx={{ minWidth: 220, px: 4 }}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Grid>
      </Grid>

      <LocationEditHistoryModal
        brandId={brandId}
        open={isEditHistoryOpen}
        onClose={() => setIsEditHistoryOpen(false)}
        location={location}
      />
    </Form>
  );
};

const LocationFormModal = ({
  open,
  onClose,
  location,
  onSave,
  groupOptions = [],
  groupSections = [],
  participantOptions = [],
  onParticipantListEndReached,
}: AnyRecord) => {
  const { selectedBrand } = useContext(GlobalContext);
  const brandId = selectedBrand?.id;

  const locationId = location?.fk_location_id;
  const isEditMode = !!locationId;
  const initialGroupIds = useMemo(() => {
    if (location?.groups?.length) {
      return location.groups
        .map((group) => group.fk_group_id)
        .filter(Boolean)
        .map(String);
    }
    if (location?.fk_group_ids?.length)
      return location.fk_group_ids.map(String);
    return location?.fk_group_id ? [String(location.fk_group_id)] : [];
  }, [location?.fk_group_id, location?.fk_group_ids, location?.groups]);
  const lockedGroupIds = useMemo(
    () => (isEditMode ? initialGroupIds : []),
    [initialGroupIds, isEditMode]
  );

  const initialValues = {
    id:
      location?.system_generated_location_id ||
      location?.system_generated_id ||
      location?.location_id ||
      "",
    created_date:
      location?.location_created_at || location?.created_date
        ? new Date(
            location.location_created_at || location.created_date
          ).toLocaleDateString()
        : "",
    last_updated_date:
      location?.location_updated_at || location?.last_updated_date
        ? new Date(
            location.location_updated_at || location.last_updated_date
          ).toLocaleDateString()
        : "",
    location_brand_id:
      location?.location_brand_unique_id ||
      location?.location_brand_id ||
      location?.unique_id ||
      "",
    location_name: location?.location_name || "",
    country: location?.country || "",
    zip_code: location?.zip_code || "",
    zip_lookup_error: false,
    state: location?.state || "",
    city: location?.city || "",
    location_street_address:
      location?.street_address || location?.location_street_address || "",
    group_id: initialGroupIds,
    landing_page_url: location?.landing_page_url || "",
    radius: location?.radius ?? "",
    geotarget: location?.geotarget || "",
    targeting_type: location ? inferTargetingType(location) : "",
    franchise_group: location?.franchise_group || "",
    dma: location?.dma || "",
    competitors: location?.competitors || "",
    local_context: location?.local_context || "",
    primary_business_goal: location?.primary_business_goal || "",
    daypart_focus: location?.daypart_focus || "",
    location_lat: location?.location_lat ?? "",
    location_long: location?.location_long ?? "",
    targeting_same_as_location: normalizeTargetingSameAsLocation(
      location?.targeting_same_as_location
    ),
    targeting_latitude: location?.targeting_latitude ?? "",
    targeting_longitude: location?.targeting_longitude ?? "",
    participant_mode: PARTICIPANT_MODE.EXISTING,
    existing_participant_id: location?.fk_participant_id
      ? String(location.fk_participant_id)
      : "",
    participant_id: location?.system_generated_participant_id || "",
    participant_first_name: location?.participant_first_name || "",
    participant_last_name: location?.participant_last_name || "",
    participant_brand_id:
      location?.participant_unique_id || location?.participant_brand_id || "",
    email_address:
      location?.participant_email ||
      location?.email_address ||
      location?.email ||
      "",
    phone: location?.participant_phone || location?.phone || "",
  };

  const handleCountryChange = (countryValue, setFieldValue) => {
    setFieldValue("state", "");
    setFieldValue("zip_code", "");
    setFieldValue("zip_lookup_error", false);
    setFieldValue("city", "");
  };

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    if (shouldBlockUnsafeSubmission(values)) {
      setSubmitting(false);
      return;
    }

    const useNewParticipant = values.participant_mode === PARTICIPANT_MODE.NEW;
    const locationIdValue = trimPayloadValue(locationId);
    const existingParticipantId = trimPayloadValue(
      values.existing_participant_id
    );
    const targetingSameAsLocation = toTargetingSameAsLocationPayloadValue(
      values.targeting_same_as_location
    );
    const targetingLatitude =
      targetingSameAsLocation === 1
        ? values.location_lat
        : values.targeting_latitude;
    const targetingLongitude =
      targetingSameAsLocation === 1
        ? values.location_long
        : values.targeting_longitude;
    const payload = {
      brand_id: brandId,
      use_new_participant: useNewParticipant,
      location_name: trimPayloadValue(values.location_name),
      street_address: trimPayloadValue(values.location_street_address),
      city: trimPayloadValue(values.city),
      state: trimPayloadValue(values.state),
      zip_code: trimPayloadValue(values.zip_code),
      country: trimPayloadValue(values.country),
      landing_page_url: trimPayloadValue(values.landing_page_url),
      radius: values.radius ? Number(values.radius) : null,
      geotarget: trimPayloadValue(values.geotarget),
      targeting_type: trimPayloadValue(values.targeting_type) || null,
      franchise_group: trimPayloadValue(values.franchise_group),
      dma: trimPayloadValue(values.dma),
      competitors: trimPayloadValue(values.competitors),
      local_context: trimPayloadValue(values.local_context),
      primary_business_goal: trimPayloadValue(values.primary_business_goal),
      daypart_focus: trimPayloadValue(values.daypart_focus),
      location_lat: toOptionalNumber(values.location_lat),
      location_long: toOptionalNumber(values.location_long),
      targeting_same_as_location: targetingSameAsLocation,
      targeting_latitude: toOptionalNumber(targetingLatitude),
      targeting_longitude: toOptionalNumber(targetingLongitude),
      fk_group_ids: (values.group_id || []).map(Number).filter(Boolean),
      unique_id: trimPayloadValue(values.location_brand_id),
      ...(useNewParticipant
        ? {
            participant_email: trimPayloadValue(values.email_address),
            participant_first_name: trimPayloadValue(
              values.participant_first_name
            ),
            participant_last_name: trimPayloadValue(
              values.participant_last_name
            ),
            participant_phone: trimPayloadValue(values.phone),
            participant_unique_id: trimPayloadValue(
              values.participant_brand_id
            ),
          }
        : {
            fk_participant_id: existingParticipantId
              ? Number(existingParticipantId)
              : null,
          }),
      ...(isEditMode &&
        !useNewParticipant && {
          participant_email: trimPayloadValue(values.email_address),
          participant_first_name: trimPayloadValue(
            values.participant_first_name
          ),
          participant_last_name: trimPayloadValue(values.participant_last_name),
          participant_phone: trimPayloadValue(values.phone),
          participant_unique_id: trimPayloadValue(values.participant_brand_id),
        }),
      ...(isEditMode && {
        fk_location_id: Number(locationIdValue) || locationIdValue,
      }),
    };

    try {
      if (isEditMode) {
        await locationManagementService.updateLocation(locationId, payload);
        toastr.success("Success", "Location updated successfully.");
      } else {
        await locationManagementService.createLocation(payload);
        toastr.success("Success", "Location added successfully.");
      }
      onSave(useNewParticipant);
      onClose();
    } catch (error) {
      setFieldError(
        "general",
        error.response?.status === 409 && isEditMode
          ? "Cannot edit location. Another user modified it less than 2 minutes ago."
          : "Failed to save location"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RpMUIDialog
      open={open}
      onClose={onClose}
      heading={isEditMode ? "Edit Location" : "Create New Location"}
      maxWidth="lg"
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "var(--radius-container)",
          maxWidth: 1186,
          width: "calc(100% - 32px)",
        },
        "& .MuiDialogContent-root": {
          borderTop: 0,
          borderBottom: 0,
          px: { xs: 2.5, md: 5.75 },
          pt: { xs: 2, md: 2 },
          pb: { xs: 3, md: 4 },
        },
        "& .MuiDialogTitle-root": {
          textAlign: "left",
          px: { xs: 2.5, md: 5.75 },
          pt: { xs: 3, md: 4 },
          pb: { xs: 1.5, md: 2 },
          fontSize: 18,
        },
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={buildLocationFormSchema([], isEditMode)}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formikProps) => (
          <>
            <LocationFormFields
              {...formikProps}
              onClose={onClose}
              location={location}
              countryOptions={LOCATION_COUNTRY_OPTIONS}
              onCountryChange={handleCountryChange}
              setFieldTouched={formikProps.setFieldTouched}
              groupOptions={groupOptions}
              groupSections={groupSections}
              participantOptions={participantOptions}
              onParticipantListEndReached={onParticipantListEndReached}
              brandId={brandId}
              lockedGroupIds={lockedGroupIds}
            />
            <FullScreenLoader
              isActive={formikProps.isSubmitting}
              message="Please wait..."
            />
          </>
        )}
      </Formik>
    </RpMUIDialog>
  );
};

export default LocationFormModal;
